/**
 * Reset Password Page
 *
 * Deterministic state machine for password reset flow.
 * Handles session verification, password update, and error states.
 *
 * States:
 * - BOOTING: Verifying recovery context + establishing session
 * - READY: Session established; show form
 * - SUBMITTING: Updating password
 * - SUCCESS: Password updated
 * - EXPIRED_OR_INVALID: Missing/expired/invalid recovery context
 * - SESSION_LOST: Session existed but disappeared mid-flow
 * - ERROR_NETWORK: Network/CORS/timeout
 * - ERROR_RATE_LIMITED: Too many attempts
 * - ERROR_UNKNOWN: Other errors
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Lock,
  XCircle,
  Clock,
  Check,
  X,
  Wifi,
  Timer,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import {
  validatePassword,
  getPasswordRequirements,
  getStrengthColor,
  getStrengthBgColor,
} from '@/core/auth/passwordValidation';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

// State machine types
type PageState =
  | 'BOOTING'
  | 'READY'
  | 'SUBMITTING'
  | 'SUCCESS'
  | 'EXPIRED_OR_INVALID'
  | 'SESSION_LOST'
  | 'ERROR_NETWORK'
  | 'ERROR_RATE_LIMITED'
  | 'ERROR_UNKNOWN';

interface SessionContext {
  userEmail: string | null;
  expiresAt: number | null; // Unix timestamp in seconds
}

// Security logging (no PHI)
function logSecurityEvent(event: string, data?: Record<string, unknown>) {
  const timestamp = new Date().toISOString();
  console.log(`[ResetPassword:${event}]`, timestamp, data ?? '');
}

// Clean auth params from URL
function cleanUrlParams() {
  if (typeof window === 'undefined') return;
  window.history.replaceState({}, document.title, '/auth/reset-password');
}

// Check for PKCE code in URL query params
function getCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}

// Check for recovery tokens in URL hash (legacy flow)
function hasHashRecoveryTokens(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash;
  return hash.includes('access_token') && hash.includes('type=recovery');
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('BOOTING');
  const [sessionContext, setSessionContext] = useState<SessionContext>({
    userEmail: null,
    expiresAt: null,
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signOutAllDevices, setSignOutAllDevices] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);

  // Prevent double exchange
  const exchangeAttempted = useRef(false);

  // Session verification on mount
  useEffect(() => {
    const verifySession = async () => {
      logSecurityEvent('RESET_BOOT_START');

      const timeoutId = setTimeout(() => {
        logSecurityEvent('RESET_SESSION_TIMEOUT');
        setPageState('ERROR_NETWORK');
      }, 3000);

      try {
        // Step 1: Check if we have a code in URL (PKCE flow)
        const code = getCodeFromUrl();
        const hasHashTokens = hasHashRecoveryTokens();

        // Step 2: Check existing session first
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          clearTimeout(timeoutId);
          logSecurityEvent('RESET_SESSION_ERROR', { code: sessionError.message });
          setPageState('EXPIRED_OR_INVALID');
          return;
        }

        // If we have a session already, use it
        if (sessionData.session) {
          clearTimeout(timeoutId);
          logSecurityEvent('RESET_SESSION_PRESENT', { hasSession: true });
          setSessionContext({
            userEmail: sessionData.session.user.email ?? null,
            expiresAt: sessionData.session.expires_at ?? null,
          });
          cleanUrlParams();
          setPageState('READY');
          return;
        }

        // Step 3: If we have a code and no session, try to exchange it
        if (code && !exchangeAttempted.current) {
          exchangeAttempted.current = true;
          logSecurityEvent('RESET_EXCHANGE_ATTEMPT');

          const { data: exchangeData, error: exchangeError } =
            await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            clearTimeout(timeoutId);
            logSecurityEvent('RESET_EXCHANGE_FAIL', { code: exchangeError.message });

            // Classify error
            const errorMsg = exchangeError.message.toLowerCase();
            if (errorMsg.includes('rate') || errorMsg.includes('too many')) {
              setPageState('ERROR_RATE_LIMITED');
            } else if (errorMsg.includes('expired') || errorMsg.includes('invalid')) {
              setPageState('EXPIRED_OR_INVALID');
            } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
              setPageState('ERROR_NETWORK');
            } else {
              setErrorCode(exchangeError.message);
              setPageState('ERROR_UNKNOWN');
            }
            return;
          }

          if (exchangeData.session) {
            clearTimeout(timeoutId);
            logSecurityEvent('RESET_EXCHANGE_SUCCESS');
            setSessionContext({
              userEmail: exchangeData.session.user.email ?? null,
              expiresAt: exchangeData.session.expires_at ?? null,
            });
            cleanUrlParams();
            setPageState('READY');
            return;
          }
        }

        // Step 4: Handle legacy hash-based tokens
        if (hasHashTokens) {
          // Let Supabase's auth state listener handle the hash tokens
          // Re-check session after a brief delay
          await new Promise((resolve) => setTimeout(resolve, 500));
          const { data: recheckData } = await supabase.auth.getSession();

          if (recheckData.session) {
            clearTimeout(timeoutId);
            logSecurityEvent('RESET_SESSION_PRESENT', { hasSession: true });
            setSessionContext({
              userEmail: recheckData.session.user.email ?? null,
              expiresAt: recheckData.session.expires_at ?? null,
            });
            cleanUrlParams();
            setPageState('READY');
            return;
          }
        }

        // No session established
        clearTimeout(timeoutId);
        logSecurityEvent('RESET_SESSION_PRESENT', { hasSession: false });
        setPageState('EXPIRED_OR_INVALID');
      } catch (err) {
        clearTimeout(timeoutId);
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        logSecurityEvent('RESET_BOOT_ERROR', { error: errorMsg });

        if (errorMsg.toLowerCase().includes('network') || errorMsg.toLowerCase().includes('fetch')) {
          setPageState('ERROR_NETWORK');
        } else {
          setErrorCode(errorMsg);
          setPageState('ERROR_UNKNOWN');
        }
      }
    };

    verifySession();
  }, []);

  // Session expiration countdown
  useEffect(() => {
    if (pageState !== 'READY' || !sessionContext.expiresAt) return;

    const updateCountdown = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = sessionContext.expiresAt! - now;
      const minutes = Math.max(0, Math.ceil(remaining / 60));
      setMinutesRemaining(minutes);

      if (remaining <= 0) {
        logSecurityEvent('RESET_SESSION_EXPIRED');
        setPageState('SESSION_LOST');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [pageState, sessionContext.expiresAt]);

  // Form validation
  const validation = validatePassword(password, sessionContext.userEmail ?? undefined);
  const requirements = getPasswordRequirements(
    password,
    confirmPassword,
    sessionContext.userEmail ?? undefined
  );
  const allRequirementsMet = requirements.every((r) => r.met);
  const canSubmit = validation.isValid && allRequirementsMet && pageState === 'READY';

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!canSubmit) return;

      setPageState('SUBMITTING');
      logSecurityEvent('RESET_UPDATE_START');

      try {
        // Verify session still exists
        const { data: sessionCheck } = await supabase.auth.getSession();
        if (!sessionCheck.session) {
          logSecurityEvent('RESET_SESSION_LOST');
          setPageState('SESSION_LOST');
          return;
        }

        // Update password
        const { error: updateError } = await supabase.auth.updateUser({
          password: password,
        });

        if (updateError) {
          logSecurityEvent('RESET_UPDATE_FAIL', { code: updateError.message });

          const errorMsg = updateError.message.toLowerCase();
          if (errorMsg.includes('rate') || errorMsg.includes('too many')) {
            setPageState('ERROR_RATE_LIMITED');
          } else if (errorMsg.includes('expired') || errorMsg.includes('session')) {
            setPageState('SESSION_LOST');
          } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
            setPageState('ERROR_NETWORK');
          } else if (errorMsg.includes('same as')) {
            setErrorCode('New password must be different from your current password.');
            setPageState('READY');
          } else {
            setErrorCode(updateError.message);
            setPageState('ERROR_UNKNOWN');
          }
          return;
        }

        logSecurityEvent('RESET_UPDATE_SUCCESS');

        // Handle sign out scope
        if (signOutAllDevices) {
          try {
            await supabase.auth.signOut({ scope: 'global' });
            logSecurityEvent('RESET_SIGNOUT_SCOPE_GLOBAL_SUCCESS');
          } catch {
            // Fallback to local scope
            await supabase.auth.signOut({ scope: 'local' });
            logSecurityEvent('RESET_SIGNOUT_SCOPE_GLOBAL_FAIL');
          }
        } else {
          await supabase.auth.signOut({ scope: 'local' });
        }

        setPageState('SUCCESS');
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        logSecurityEvent('RESET_UPDATE_ERROR', { error: errorMsg });

        if (errorMsg.toLowerCase().includes('network') || errorMsg.toLowerCase().includes('fetch')) {
          setPageState('ERROR_NETWORK');
        } else {
          setErrorCode(errorMsg);
          setPageState('ERROR_UNKNOWN');
        }
      }
    },
    [canSubmit, password, signOutAllDevices]
  );

  // Retry handler
  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  // BOOTING state
  if (pageState === 'BOOTING') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // EXPIRED_OR_INVALID state
  if (pageState === 'EXPIRED_OR_INVALID') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Reset Link Invalid or Expired</h1>
            <p className="text-muted-foreground mb-6">
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <div className="space-y-2">
              <Link to="/auth/forgot-password">
                <Button className="w-full">Request a New Reset Link</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SESSION_LOST state
  if (pageState === 'SESSION_LOST') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <Timer className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-6">
              Your recovery session was lost. Please request a new reset link.
            </p>
            <div className="space-y-2">
              <Link to="/auth/forgot-password">
                <Button className="w-full">Request a New Reset Link</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ERROR_NETWORK state
  if (pageState === 'ERROR_NETWORK') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <Wifi className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Connection Error</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't reach the server. Check your connection and try again.
            </p>
            <div className="space-y-2">
              <Button onClick={handleRetry} className="w-full">
                Retry
              </Button>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ERROR_RATE_LIMITED state
  if (pageState === 'ERROR_RATE_LIMITED') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Too Many Attempts</h1>
            <p className="text-muted-foreground mb-6">
              Too many attempts. Please wait a few minutes and try again.
            </p>
            <div className="space-y-2">
              <Link to="/login">
                <Button className="w-full">Back to Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ERROR_UNKNOWN state
  if (pageState === 'ERROR_UNKNOWN') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-4">
              Something went wrong while resetting your password.
            </p>
            {errorCode && (
              <p className="text-xs text-muted-foreground mb-6 font-mono bg-muted/50 p-2 rounded">
                Error: {errorCode}
              </p>
            )}
            <div className="space-y-2">
              <Button onClick={handleRetry} className="w-full">
                Retry
              </Button>
              <Link to="/auth/forgot-password">
                <Button variant="outline" className="w-full">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS state
  if (pageState === 'SUCCESS') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Password Updated</h1>
            <p className="text-muted-foreground mb-6">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
            <Button onClick={() => navigate('/login', { replace: true })} className="w-full">
              Sign In
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-4">
              Need help? Email <a className="text-primary hover:underline" href="mailto:support.ai">support.ai</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // READY and SUBMITTING states (form)
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-strong rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center space-x-2 mb-4">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold">Set New Password</h1>
            <p className="text-muted-foreground mt-2">Choose a strong password for your account.</p>
          </div>

          {/* User email display */}
          {sessionContext.userEmail && (
            <div className="bg-muted/50 rounded-lg px-4 py-3 mb-6 text-center">
              <p className="text-sm text-muted-foreground">Resetting password for:</p>
              <p className="font-medium">{sessionContext.userEmail}</p>
            </div>
          )}

          {/* Session expiration indicator */}
          {minutesRemaining !== null && (
            <div
              className={`flex items-center justify-center gap-2 text-sm mb-6 ${
                minutesRemaining < 10 ? 'text-amber-500' : 'text-muted-foreground'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>
                Session expires in ~{minutesRemaining} minute{minutesRemaining !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New password field */}
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={pageState === 'SUBMITTING'}
                  required
                  className="bg-muted/50 pl-10 pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength indicator */}
              {password && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Password strength</span>
                    <span className={getStrengthColor(validation.strength)}>
                      {validation.strength.charAt(0).toUpperCase() + validation.strength.slice(1)}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${getStrengthBgColor(validation.strength)}`}
                      style={{ width: `${validation.score}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm password field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={pageState === 'SUBMITTING'}
                  required
                  className="bg-muted/50 pl-10"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Password requirements checklist */}
            {password && (
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm font-medium mb-3">Password Requirements</p>
                <div className="space-y-2">
                  {requirements.map((req) => (
                    <div key={req.id} className="flex items-center gap-2 text-sm">
                      {req.met ? (
                        <Check className="w-4 h-4 text-green-500 shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                      <span className={req.met ? 'text-foreground' : 'text-muted-foreground'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sign out all devices checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="signOutAll"
                checked={signOutAllDevices}
                onCheckedChange={(checked) => setSignOutAllDevices(checked === true)}
                disabled={pageState === 'SUBMITTING'}
              />
              <Label htmlFor="signOutAll" className="text-sm font-normal cursor-pointer">
                Sign out of all other devices
              </Label>
            </div>

            {/* Error display */}
            {errorCode && pageState === 'READY' && (
              <p className="text-sm text-destructive">{errorCode}</p>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={!canSubmit || pageState === 'SUBMITTING'}
            >
              {pageState === 'SUBMITTING' ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
